import { inject } from '@angular/core';
import { CanActivateFn, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { UserAccess } from '@interfaces';
import { AppConstants } from 'src/app/app.constants';
import { ErrorManagerService, ErrorObject, MaintenanceService } from '@core';
import { AppInsightsService, AppCenterAnalytics, SharedService } from '@services';
import { MatDialog } from '@angular/material/dialog';
import { DowntimeDialogComponent } from '@shared';
// The updated UserAccessGuard using CanActivateFn
export const UserAccessGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree => {

  // Inject services using inject() function
  const sharedService = inject(SharedService);
  const errorManager = inject(ErrorManagerService);
  const appInsightsService = inject(AppInsightsService);
  const dialog = inject(MatDialog);
  const maintenance = inject(MaintenanceService);
  const router = inject(Router); // Inject the router for navigation

  // Get access
  return getAccess(sharedService).pipe(map((res) => {
    appInsightsService.trackPageView(AppCenterAnalytics.GET_USER_ACCESS_TYPE_API_SUCCESS, { Response: res });
    if (!res.isOLAUser && !res.isReportsUser) {
      // User has no access to any module
      const error: ErrorObject = { message: AppConstants.ERROR.MESSAGES.NO_ACCESS, component: 'UserAccessGuard' };
      errorManager.throwError(error);
      return false;
    }

    // Show maintenance dialog if required
    !maintenance.isOutageDialogDisplayed ? showMaintenanceMessage(maintenance, dialog, appInsightsService) : null;

    sessionStorage.setItem(AppConstants.LABELS.userName, res.userName);

    localStorage.setItem(AppConstants.LABELS.hasReportsAccess, res.isReportsUser ? 'YES' : 'NO');
    localStorage.setItem(AppConstants.LABELS.hasManagementAccess, res.isOLAUser ? 'YES' : 'NO');

    if (res.isOLAUser) {
      sharedService.updateAccessLevel(res.olaUserType);
    }

    // Redirect based on access
    if (res.isOLAUser && res.isReportsUser) {
      return true;
    } else {
      if (res.isReportsUser) {
        return router.createUrlTree(['/main']); // Use Router to create a valid UrlTree
      }

      if (res.isOLAUser) {
        return true;
      }
      return false;
    }
  }, (error) => {
    appInsightsService.trackPageView(AppCenterAnalytics.GET_USER_ACCESS_TYPE_API_ERROR, { Response: error });
  }));
};

// Function to get access data
function getAccess(sharedService: SharedService): Observable<UserAccess> {
  return sharedService.getUserAccessRoles();
}

// Function to display maintenance message
function showMaintenanceMessage(
  maintenance: MaintenanceService,
  dialog: MatDialog,
  appInsightsService: AppInsightsService
): void {
  maintenance.showOutagePopup().pipe(take(1), switchMap((res: { show: boolean, title: string, message: string, is_complete_shutdown: boolean }) => {
    if (res.show) {
      appInsightsService.trackPageView(AppCenterAnalytics.PLANNED_OUTAGE_MSG_DISPLAYED, {
        data: {
          title: res.title,
          displayed_message: res.message,
          isCompleteShutdown: res.is_complete_shutdown
        }
      });
      const dialogRef = dialog.open(DowntimeDialogComponent, {
        disableClose: true,
        data: { message: res.message, title: res.title, outageFlag: res.is_complete_shutdown },
        panelClass: 'downtime-dialog-panel'
      });
      maintenance.isOutageDialogDisplayed = true;
      return dialogRef.afterClosed().pipe(take(1), tap(() => {
        appInsightsService.trackPageView(AppCenterAnalytics.PLANNED_OUTAGE_MSG_CLOSED);
      }));
    }
    return of(false);
  }), catchError(() => {
    return of(false);
  })).subscribe();
}
